import { html, nothing, TemplateResult } from 'lit';
import { WEB_COMPONENT_HEALTH_DASHBOARD_OVERVIEW } from '../../../../webcomponents.constants';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { OverviewItem, OverviewStringsSection } from '../types';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { isEmpty } from 'lodash';
import { formatLargeNumber } from '../../../../utils/formatter.utils';
import { WidgetWithOverviewData } from '@nirvana/shared/dtos/types/widgets/common';

@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_OVERVIEW)
export class GzHealthDashboardOverviewWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true, reflect: true })
  payload: WidgetWithOverviewData;

  changedInfoBoxText = `
    <gz-tooltip isEllipsis tooltipText="${translateFn('You have <b>updates</b> for this widget!')}">
      ${translateFn('You have <b>updates</b> for this widget!')}
    </gz-tooltip>
    <br />
    <gz-tooltip
      isEllipsis
      class="three-line-tooltip"
      tooltipText="${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}"
    >
      ${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}
    </gz-tooltip>
  `;

  strings: OverviewStringsSection = {
    items: [
      {
        key: 'managedEndpoints',
        name: translateFn('Managed endpoints'),
      },
      {
        key: 'activeEndpoints',
        name: translateFn('Active endpoints'),
      },
      {
        key: 'offlineEndpoints',
        name: translateFn('Offline endpoints'),
      },
      {
        key: 'unmanagedEndpoints',
        name: translateFn('Unmanaged endpoints'),
      },
    ],
  };

  static get styles() {
    return styles;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-container">
        ${this.strings.items.map((item, index) => this.renderLoaderItem(item, index))}
      </div>
    `;
  }

  private renderLoaderItem(item: OverviewItem, index: number): TemplateResult {
    return html`
      <div class="loader-item">
        <span class="loader-name fs-16">${item.name}</span>
        <gz-loader></gz-loader>
      </div>
      ${this.shouldAddSeparator(index)}
    `;
  }

  protected renderContent(): TemplateResult {
    return html`
      <div id="healthDashboardOverview">
        ${this.strings.items.map((item, index) => this.renderItem(item, index))}
        ${this.renderChangedInfoBox(this.changedInfoBoxText)}
      </div
    `;
  }

  protected renderItem(item: OverviewItem, index: number): TemplateResult {
    const value =
      !isEmpty(this.payload) && this.payload.inventoryOverview ? this.payload.inventoryOverview[item.key] : 0;
    const displayValue = this.state === WidgetStateFrontEnd.IS_ACTIVE ? formatLargeNumber(value) : html`_&nbsp_`;

    return html` <div class="overview-section">
        <div class="section-title fs-16">
          <span class="section-title">${item.name}${this.renderErrorIcon()}</span>
        </div>
        <span class="fs-48 section-value">${displayValue}</span>
      </div>
      ${this.shouldAddSeparator(index)}`;
  }

  protected renderChangedInfoBox(text?: string): TemplateResult | undefined {
    if (this.state !== WidgetStateFrontEnd.IS_CHANGED) {
      return;
    }
    return html`<div class="changed-info-box">${super.renderChangedInfoBox(text)}</div>`;
  }

  private renderErrorIcon() {
    const tooltipError = translateFn(
      'An internal error prevented the widget from displaying data. Please contact Bitdefender Enterprise Support for more information.'
    );
    return this.state === WidgetStateFrontEnd.IS_ERROR
      ? html`<gz-tooltip tooltipText="${tooltipError}" class="error-tooltip">
          <gz-icon name="ds-icon-notification_negative" size="24" color="var(--red-layout-300-primary)"></gz-icon>
        </gz-tooltip>`
      : nothing;
  }

  private shouldAddSeparator(index: number) {
    const numKeys = this.strings ? this.strings.items.length : 0;
    const addSeparator = (index !== undefined && index + 1) < numKeys;
    return addSeparator ? html`<div class="separator"></div>` : nothing;
  }
}
